import { useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover';
import { cn } from '@/app/components/ui/utils';
import { useProjectCostCodes } from '@/hooks/use-projects';

interface CostCodeSelectorProps {
  projectId: number;
  value?: number | null;
  onChange: (costCodeId: number | null) => void;
  disabled?: boolean;
  enabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function CostCodeSelector({
  projectId,
  value,
  onChange,
  disabled = false,
  enabled = true,
  placeholder = 'Select cost code...',
  className,
}: CostCodeSelectorProps) {
  const [open, setOpen] = useState(false);
  const { data: costCodes, isLoading, isError } = useProjectCostCodes(projectId, enabled);

  const selectedCode = costCodes?.find((cc) => cc.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || projectId <= 0}
          className={cn(
            'w-full justify-between',
            !selectedCode && 'text-muted-foreground',
            className,
          )}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </span>
          ) : selectedCode ? (
            <span className="truncate">
              {selectedCode.code} — {selectedCode.name}
            </span>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by code or name..." />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? 'Loading cost codes...'
                : isError
                  ? 'Failed to load cost codes.'
                  : 'No cost codes found.'}
            </CommandEmpty>
            <CommandGroup>
              {(costCodes ?? []).map((cc) => (
                <CommandItem
                  key={cc.id}
                  value={`${cc.code} ${cc.name}`}
                  onSelect={() => {
                    onChange(cc.id === value ? null : cc.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === cc.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{cc.code} — {cc.name}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                        {cc.category}
                      </Badge>
                      {cc.description && (
                        <span className="truncate">{cc.description}</span>
                      )}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
